import { FC, ReactNode, useEffect, useState } from "react";
import "./BannerWidget.css";

const DEFAULT_STORAGE_KEY = "dashboardBannerAck";

const BannerWidget: FC<{
  id: string;
  title: string;
  message: string | ReactNode;
  acknowledgeLabel?: string;
  checkboxLabel?: string;
  // Optional image rendered to the left of the message/actions column.
  image?: ReactNode;
  // When false, the acknowledgement checkbox is hidden and the button is
  // always enabled. Defaults to true to preserve the original API.
  requireCheckbox?: boolean;
  storageKey?: string;
  // Server-side list of banner ids the current user has already acknowledged.
  // When provided, drives visibility instead of localStorage and the
  // acknowledge action defers persistence to `onAcknowledge`.
  acknowledgedIds?: string[];
  onAcknowledge?: (id: string) => void;
}> = ({
  id,
  title,
  message,
  acknowledgeLabel = "I Acknowledge",
  checkboxLabel = "I have read and understood the above",
  image,
  requireCheckbox = true,
  storageKey = DEFAULT_STORAGE_KEY,
  acknowledgedIds,
  onAcknowledge = () => {},
}) => {
  // Derive initial visibility synchronously from props so the first paint is
  // already correct — otherwise the banner briefly flashes before an effect
  // could flip it off.
  const computeInitialVisible = () => {
    if (acknowledgedIds !== undefined) {
      return !acknowledgedIds.includes(id);
    }
    try {
      return localStorage.getItem(storageKey) !== id;
    } catch {
      return true;
    }
  };
  const [visible, setVisible] = useState<boolean>(computeInitialVisible);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    // React to later prop changes (e.g. acknowledgedIds updates after a
    // server round-trip, or the id prop is bumped to re-prompt).
    if (acknowledgedIds !== undefined) {
      if (acknowledgedIds.includes(id)) {
        setVisible(false);
      } else {
        setVisible(true);
        setChecked(false);
      }
      return;
    }
    try {
      const acked = localStorage.getItem(storageKey);
      if (acked === id) {
        setVisible(false);
      } else {
        setVisible(true);
        setChecked(false);
      }
    } catch {
      setVisible(true);
    }
  }, [id, storageKey, acknowledgedIds]);

  const handleAcknowledge = () => {
    if (acknowledgedIds === undefined) {
      try {
        localStorage.setItem(storageKey, id);
      } catch {
        // Ignore storage failures; banner will simply reappear on next load.
      }
    }
    setVisible(false);
    onAcknowledge(id);
  };

  const handleDismiss = () => {
    // Session-only dismiss; no localStorage write so it returns on next load
    // until the user explicitly acknowledges.
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="card-banner rounded p-4 mb-5">
      <i
        className="bi bi-x-lg banner-dismiss"
        role="button"
        aria-label="Dismiss"
        onClick={handleDismiss}
      ></i>
      <div className="banner-title">{title}</div>
      <div className="banner-body">
        {image && <div className="banner-image">{image}</div>}
        <div className="banner-content">
          <div className="banner-message">{message}</div>
          <div className="banner-actions">
            {requireCheckbox && (
              <label className="banner-ack-label">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                />
                {checkboxLabel}
              </label>
            )}
            <button
              type="button"
              className="btn btn-ll"
              disabled={requireCheckbox && !checked}
              onClick={handleAcknowledge}
            >
              {acknowledgeLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { BannerWidget };
